import * as React from 'react';
import { SegmentedControl } from '@/components/ui/workspace';
import { AiConfidenceChip } from '@/components/ui/ai-confidence-chip';
import { AiSurface } from '@/components/ui/ai-surface';
import { CitationChip } from '@/components/ui/citation-chip';
import { ModuleSelect, type ModuleOption } from '@/components/requirement-import/module-select';
import { cn } from '@/lib/utils/cn';
import type { IntakeCandidate } from '@/lib/api/requirement-library-intake';
import {
  citationLocation,
  documentLabel,
  locationChip,
  matchHeading,
  matchKindLabel,
} from './intake-logic';

/**
 * One candidate requirement in the AI document intake review (T-0154).
 *
 * Reference: the `.cand` card of `renderAi` in `docs/requirement-library/
 * prototype/selection.js`. Left: the text as the document words it, a
 * citation chip per passage, the confidence, a low-confidence marker, and
 * the requirements it resembles. Right: where it will be filed and the
 * decision.
 *
 * The card is an AI surface: a candidate is AI-derived content and is
 * never styled as an ordinary row. Confidence and citations use the
 * shared chips. Every figure shown is the server's.
 */

const DIFF_BOX =
  'rounded-card border border-l-4 border-accent-amber-100 border-l-accent-amber-600 bg-accent-amber-50 px-3 py-2.5 text-body-sm text-ink-700';
const CHIP = 'inline-flex h-[22px] items-center rounded-full px-2.5 text-meta font-medium';

type Decision = 'accept' | 'skip';

export interface IntakeCandidateCardProps {
  candidate: IntakeCandidate;
  fileName: string;
  /**
   * The document's `page_source` (T-0155), for citations from a server that
   * sent no location. Current servers send a location with every citation.
   */
  pageSource?: string | null;
  /** Active modules, for the selector. */
  modules: ReadonlyArray<ModuleOption>;
  /** Sub-categories each module already has, for the sub-category field. */
  subcategories: ReadonlyArray<string>;
  /** A change to this candidate is being saved. */
  saving: boolean;
  /** The run is closed: show what was decided, change nothing. */
  readOnly: boolean;
  /** The last change to this candidate was refused. */
  error: string | null;
  /** This candidate stopped the last confirm. */
  flagged: boolean;
  onDecide: (decision: 'accept' | 'skip' | 'undecided') => void;
  onModule: (moduleId: string) => void;
  onSubcategory: (subcategory: string) => void;
  onOpenSource: (citationIndex: number) => void;
}

export function IntakeCandidateCard({
  candidate: c,
  fileName,
  pageSource,
  modules,
  subcategories,
  saving,
  readOnly,
  error,
  flagged,
  onDecide,
  onModule,
  onSubcategory,
  onOpenSource,
}: IntakeCandidateCardProps) {
  const listId = React.useId();
  const decision: Decision | null =
    c.decision === 'accept' || c.decision === 'skip' ? c.decision : null;
  const suggested = c.module_id != null && c.module_id === c.suggested.module_id;
  const label = documentLabel(fileName);
  const name = `candidate ${c.ordinal}`;

  return (
    <AiSurface
      as="article"
      aria-label={`Candidate ${c.ordinal}`}
      data-decision={c.decision}
      className={cn(
        'grid grid-cols-1 gap-5 rounded-card border-l-4 px-4 py-3.5 md:grid-cols-[minmax(0,1fr)_260px]',
        decision === 'accept' && 'border-l-posture-healthy',
        decision === 'skip' && 'border-l-ink-200 opacity-60',
        flagged && 'outline outline-2 outline-posture-critical',
      )}
    >
      <div className="flex min-w-0 flex-col gap-2.5">
        <p className="text-body leading-[22px] text-ink-900">{c.text}</p>
        <div className="flex flex-wrap items-center gap-2">
          {c.citations.map((cite, i) => {
            const location = citationLocation(cite, pageSource);
            return (
              <CitationChip
                key={`${cite.page_number}:${cite.start}`}
                cite={{
                  id: `${c.id}:${i}`,
                  doc_label: label,
                  ...locationChip(location),
                  confidence: c.confidence,
                }}
                onOpen={() => onOpenSource(i)}
              />
            );
          })}
          <AiConfidenceChip value={c.confidence} />
          {c.low_confidence ? (
            <span className={cn(CHIP, 'bg-posture-critical-tint text-posture-critical')}>
              Low confidence, may not be a requirement
            </span>
          ) : null}
          {c.citations.length > 1 ? (
            <span className="text-meta text-ink-500">
              Stated {c.citations.length} times in the document
            </span>
          ) : null}
        </div>

        {c.previously_accepted ? (
          <div className={DIFF_BOX} role="group" aria-label="Already in the library">
            <span className="font-semibold text-ink-900">
              Already added from this document
              {c.previously_accepted.number ? (
                <>
                  {' as '}
                  <span className="font-mono text-meta">{c.previously_accepted.number}</span>
                </>
              ) : null}
            </span>{' '}
            in an earlier import.
          </div>
        ) : null}

        {c.matches.length > 0 ? (
          <div className={DIFF_BOX} role="group" aria-label="Possible duplicate">
            {c.matches.map((m, i) => (
              <div
                key={m.item_id}
                className={i > 0 ? 'mt-2 border-t border-accent-amber-100 pt-2' : ''}
              >
                <div className="mb-1 font-semibold text-ink-900">
                  {matchHeading(m)} <span className="font-mono text-meta">{m.number}</span> in{' '}
                  {m.module_name}
                  <span className="font-normal text-ink-700">
                    {' · '}
                    {matchKindLabel(m)}
                    {m.in_draft ? ' · in an unpublished draft' : ''}
                  </span>
                </div>
                {m.text}
              </div>
            ))}
          </div>
        ) : null}

        {c.similar_in_document.length > 0 ? (
          <div className={DIFF_BOX} role="group" aria-label="Similar in this document">
            {c.similar_in_document.map((m, i) => (
              <div
                key={m.ordinal}
                className={i > 0 ? 'mt-2 border-t border-accent-amber-100 pt-2' : ''}
              >
                <div className="mb-1 font-semibold text-ink-900">
                  Similar to candidate {m.ordinal} of this document
                  <span className="font-normal text-ink-700">
                    {' · '}
                    {matchKindLabel(m)}
                  </span>
                </div>
                {m.text}
              </div>
            ))}
          </div>
        ) : null}

        {error ? (
          <p role="alert" className="text-meta font-medium text-posture-critical">
            {error}
          </p>
        ) : null}
      </div>

      <div className="flex min-w-0 flex-col gap-1.5">
        <div className="flex items-center gap-1.5 text-meta font-medium text-ink-700">
          Module
          {suggested ? (
            <span className="inline-flex h-[18px] items-center rounded-full border border-ai-surface-border bg-ai-surface px-1.5 text-[10px] font-medium text-ai-ink">
              Suggested
            </span>
          ) : null}
        </div>
        {readOnly ? (
          <div className="text-body-sm text-ink-900">{c.module_name ?? 'No module'}</div>
        ) : (
          <ModuleSelect
            aria-label={`Module for ${name}`}
            value={c.module_id}
            modules={modules}
            allowSkip={false}
            required
            disabled={saving}
            onChange={onModule}
          />
        )}
        <label className="mt-1 flex flex-col gap-1 text-meta font-medium text-ink-700">
          Sub-category
          {readOnly ? (
            <span className="text-body-sm font-normal text-ink-900">{c.subcategory ?? 'None'}</span>
          ) : (
            <input
              // Follows the module: remount when the server re-derives it.
              key={`${c.module_id ?? ''}:${c.subcategory ?? ''}`}
              aria-label={`Sub-category for ${name}`}
              defaultValue={c.subcategory ?? ''}
              list={listId}
              disabled={saving || c.module_id == null}
              maxLength={120}
              onKeyDown={(e) => {
                if (e.key === 'Enter') e.currentTarget.blur();
                if (e.key === 'Escape') {
                  e.stopPropagation();
                  e.currentTarget.value = c.subcategory ?? '';
                  e.currentTarget.blur();
                }
              }}
              onBlur={(e) => {
                const next = e.currentTarget.value.trim();
                if (next && next !== (c.subcategory ?? '')) onSubcategory(next);
                else e.currentTarget.value = c.subcategory ?? '';
              }}
              className="h-[30px] w-full rounded-card border border-ink-200 bg-paper-0 px-2.5 text-body-sm font-normal text-ink-900 focus:outline focus:outline-2 focus:outline-offset-1 focus:outline-accent-amber-600 disabled:opacity-50"
            />
          )}
        </label>
        <datalist id={listId}>
          {subcategories.map((s) => (
            <option key={s} value={s} />
          ))}
        </datalist>

        {readOnly ? (
          <div className="mt-2 text-body-sm font-medium text-ink-900">
            {c.result ? (
              <>
                Added
                {c.result.number ? (
                  <>
                    {' as '}
                    <span className="font-mono text-meta">{c.result.number}</span>
                  </>
                ) : null}
              </>
            ) : decision === 'accept' ? (
              'Accepted'
            ) : decision === 'skip' ? (
              'Skipped'
            ) : (
              'Not decided'
            )}
          </div>
        ) : (
          <>
            <SegmentedControl<Decision>
              aria-label={`Decision for ${name}`}
              className="mt-2 flex [&>button]:flex-1"
              value={decision}
              options={[
                { value: 'accept', label: c.has_duplicate ? 'Add anyway' : 'Accept' },
                { value: 'skip', label: 'Skip' },
              ]}
              onChange={(next) => {
                if (saving) return;
                // Choosing the same decision again takes it back.
                onDecide(next === decision ? 'undecided' : next);
              }}
            />
            {c.module_id == null ? (
              <p className="text-meta text-ink-500">Choose a module to accept this candidate.</p>
            ) : null}
          </>
        )}
      </div>
    </AiSurface>
  );
}
